import { Container } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useA2HS } from "../../hooks/useA2HS";
import { BoardSearchListState, ImgViewer, MoveButtonState } from "../../recoil/states";
import { asyncReqBoardInfo } from "../../reducers/BoardInfoReducer";
import { LoadingBarActions } from "../../reducers/LoadingBarReducer";
import { AppDispatch } from "../../Store";
import CommonUtil from "../../utils/CommonUtil";
import LoginBtns from "../form/LoginBtns";
import TopMainBtns, { FcRef } from "../form/TopMainBtns";
import TopSearch from "../form/TopSearch";

declare global {
    interface Window {
        procNavigate: (url: string) => void;
        procUrlChange: (isPrevPress: boolean) => void;
        showRandomBtns: () => void;
    }
}

const GnbSearch = () => {

    const setBoardSearchListState = useSetRecoilState(BoardSearchListState); // 검색 화면 페이징 처리를 위해 사용되는 state
    const setMoveButtonState = useSetRecoilState(MoveButtonState); // '순차 / 랜덤' 버튼 클릭 여부 정보를 갖는 state
    const [imgViewer, setImgViewer] = useRecoilState(ImgViewer); // 이미지 뷰어 UI 랜더링 처리를 위해 사용되는 state

    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const { canInstall, promptInstall, isIOS, isStandalone } = useA2HS();
    const [showIosGuide, setShowIosGuide] = useState(false);

    // 최신 값으로 동기화
    const imgViewerOpenRef = useRef(imgViewer.open);
    useEffect(() => {
        imgViewerOpenRef.current = imgViewer.open;
    }, [imgViewer.open]);

    const ignoreNextPopRef = useRef(false);

    // iOS Safari 여부(크롬/파폭/엣지(WKWebView)는 제외)
    const isIOSSafari = (() => {
        const ua = navigator.userAgent.toLowerCase();
        const isiOS = /iphone|ipad|ipod/.test(ua);
        const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
        return isiOS && isSafari;
    })();

    const promptInstallIOS = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isIOS || isStandalone) return;
        setShowIosGuide(true); // 가이드는 항상 띄워 힌트 제공
        if (isIOSSafari && (navigator as any).share) {
            try {
                // 공유 시트를 열면 사용자 액션으로 '홈 화면에 추가' 접근 가능
                const shareUrl = typeof window !== "undefined" && window.location ? window.location.href : "/";
                await (navigator as any).share({ title: document.title, url: shareUrl });
            } catch {
                // 사용자가 취소해도 가이드는 떠 있으므로 추가 처리 불필요
            }
        }
        // iOS 크롬/파폭 등은 Safari에서만 A2HS 가능 -> 가이드가 안내
    };

    useEffect(() => {

        // 앵귤러그리드 스크립트 영역에서 리액트 화면 이동을 요청하는 함수
        window.procNavigate = (url: string) => {
            if (navigator.userAgent.indexOf(";UGGIYO_APP;") > -1) {
                // 앱 내 호출일 경우
                const AppWebView = (window as any).AppWebView;
                if (typeof (AppWebView) !== "undefined") {
                    if (typeof (AppWebView.show_New_Comment_Detail) === "function") {

                        const params = new URL(url, window.location.origin).searchParams;
                        const board_seq = params.get("seq");
                        AppWebView.show_New_Comment_Detail(board_seq, "");
                    }
                }
            }
            else {
                // 그 외
                (window as any).urlChange(url);
            }
        }

        // 뒤로가기 or 앞으로가기 감지 후, 해당 URL로 라우팅 처리하는 로직
        window.procUrlChange = (isPrevPress: boolean) => {

            if (window.location.pathname === "/board_info") {
                // '웃긴사진' 화면 일 경우

                // 웃긴사진 고유번호 파라메터가 없어서 리다이렉션 된 URL일 경우, 히스토리 한 단계 건너 뛰도록 처리
                const view_type = CommonUtil.getQuerystring("view_type", "CURR");

                if (view_type !== "CURR") {
                    navigate(isPrevPress ? -1 : 1);
                    return;
                }

                // 리렌더링 요청
                const board_seq = CommonUtil.getQuerystring("seq", ""); // '게시판 고유번호' 파라메터 가져오기
                dispatch(asyncReqBoardInfo({ view_type, board_seq })); // 게시물 상세 정보 가져오기
            }
            else if (window.location.pathname === "/board_search_list") {
                // '검색' 화면 일 경우

                const curr_page = parseInt(CommonUtil.getQuerystring("curr_page", "1"), 10); // '페이지 번호' 파라메터 가져오기
                setBoardSearchListState(rawState => ({ ...rawState, currPage: curr_page })); // 검색 화면 렌더링 요청
            }
        }

        window.showRandomBtns = () => {
            // '랜덤' 버튼 클릭 알림 (마지막으로 본 고유번호 갱신되지 않도록 하기 위함)
            setMoveButtonState(rawState => ({ ...rawState, isClickBtnOrder: false, isClickBtnRandom: true }));
        }

        const onPop = (e: PopStateEvent) => {
            // 동영상 전체화면 해제 or 이미지 뷰어 닫기 후, 재귀 호출 된 경우 하위로직 1회만 skip!
            if (ignoreNextPopRef.current) {
                ignoreNextPopRef.current = false;
                setTimeout(() => {
                    CommonUtil.scrollTo("header", -7, 0); // 상단 메뉴 영역 안보이게 스크롤바 자동 이동
                }, 0);
                return;
            }

            // 구글 전면광고의 히스토리/해시 변화는 **절대 건드리지 않음**
            // 애드센스 전면광고가 호출 중인지 여부 확인
            if (
                window.location.hash === '#google_vignette' ||
                /[#?&]google_vignette\b/.test(window.location.href)
            ) return;

            const cur_hist_idx = e?.state?.idx || 0;

            let isPrevPress = false; // '뒤로가기'가 실행됐는지 여부 (뒤로가기 : true, 앞으로가기 : false)
            const prev_hist_idx = (window as any).getHistIdx();
            if (prev_hist_idx === 0 || cur_hist_idx < prev_hist_idx) isPrevPress = true; // '뒤로가기'가 실행된 경우

            let intercepted = imgViewerOpenRef.current; // 이미지 확대/축소 팝업이 표시 중일 경우, 팝업 닫기 처리
            if (intercepted) setImgViewer(v => ({ ...v, open: false }));
            else intercepted = (window as any).videoMinAndPause?.(); // 동영상 전체화면 모드일 경우, 전체화면 해제 및 재생중지 처리
            if (intercepted) {
                ignoreNextPopRef.current = true; // 'window.history.go' 실행에 의해 'window.onpopstate' 가 재귀 호출되기 때문에 재샐행 안되도록 플래그 값 저장
                setTimeout(() => {
                    try { window.history.go(isPrevPress ? 1 : -1); } catch { } // 뒤로 들어왔으면 +1로 복구, 앞으로 들어왔으면 -1로 복구
                }, 0);
                return; // 하위 로직 skip
            }

            dispatch(LoadingBarActions.reset()); // 로딩바 숨김
            window.procUrlChange(isPrevPress); // 라우팅 처리
            window.showRandomBtns(); // 하단 랜덤 버튼으로 변경

            // 이전 페이지 데이터 저장
            (window as any).setHistIdx(cur_hist_idx);
        }
        window.addEventListener('popstate', onPop);

        return () => {
            window.removeEventListener('popstate', onPop); // 이중으로 등록되는 경우가 있어서 해제 처리
        };
    }, []);

    const topMainBtnsRef = useRef<FcRef>(null); // TopMainBtns 컴포넌트 참조 변수

    /**
    * 안드로이드 마켓으로 이동 함수
    * 
    * @param 
    * @returns 
    */
    const move_Android_Market = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {

        e.preventDefault();

        if (
            navigator.userAgent.indexOf("Android") > -1
        ) {
            // 안드로이드 기기일 경우

            window.location.href = "market://details?id=kr.co.wlv.m.uggiyo";
        }
        else {
            // 그 외

            window.open("https://play.google.com/store/apps/details?id=kr.co.wlv.m.uggiyo", "");
        }
    }

    return (
        <div className="hd" style={navigator.userAgent.indexOf(";UGGIYO_APP;") === -1 ? {} : { display: "none" }}>
            <Container maxWidth={false} className="root_max_width">
                <div className="hd_wrapper">
                    <div className="hd_wr" style={{ width: "100%" }}>
                        <div className="logo">
                            <Link to="/board_info?view_type=RANDOM" onClick={(e) => topMainBtnsRef.current?.handlerTopBtns(e, "BOARD")}>
                                <img src="/images/top_logo.png" alt="우끼요" />&nbsp;
                                <span className="hanna">
                                    <span className="hanna normal w400" style={{ fontSize: "2.4em", color: "#ffffff" }}>우끼요</span>
                                </span>
                            </Link>
                        </div>
                        <TopSearch />
                    </div>
                </div>
                <nav className="gnb">
                    <h2>메인메뉴</h2>
                    <div className="gnb_wrap" style={{ width: "100%" }}>
                        <TopMainBtns ref={topMainBtnsRef} />
                        <LoginBtns />

                        {/* 데스크톱 / 안드로이드 크롬 브라우저에 웹 앱 설치 시 필요한 소스코드 영역 */}
                        {/* 1. Android/Chrome: 이벤트가 오면 버튼 노출 */}
                        {
                            canInstall &&
                            <a href="#none" className="btn_app_download" onClick={promptInstall}>
                                <div>
                                    여기를 클릭해서 '우끼요 WEB'을 설치하세요.
                                </div>
                            </a>
                        }
                        {/* 2. iOS: Safari에서는 공유 시트를 띄워 설치 유도  가이드 오버레이 */}
                        {isIOS && !isStandalone && (
                            <>
                                <a href="#none" className="btn_app_download" onClick={promptInstallIOS}>
                                    <div>
                                        여기를 클릭 ▸ '홈 화면에 추가'를 찾아 앱을 설치하세요.
                                    </div>
                                </a>
                                {showIosGuide && (
                                    <div
                                        style={{
                                            position: "fixed",
                                            inset: 0,
                                            background: "rgba(0,0,0,0.6)",
                                            zIndex: 9999,
                                            display: "flex",
                                            alignItems: "flex-end",
                                            justifyContent: "center",
                                            pointerEvents: "auto"
                                        }}
                                        onClick={() => setShowIosGuide(false)}
                                    >
                                        <div
                                            style={{
                                                width: "100%",
                                                maxWidth: 480,
                                                background: "#fff",
                                                color: "#222",
                                                borderTopLeftRadius: 12,
                                                borderTopRightRadius: 12,
                                                padding: "16px 20px",
                                                boxSizing: "border-box"
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div style={{ fontWeight: 700, marginBottom: 8 }}>iOS 설치 안내</div>
                                            <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                                                <li>아래 <b>공유(□↑)</b> 버튼을 누릅니다.</li>
                                                <li><b>홈 화면에 추가</b>를 선택합니다.</li>
                                                {!isIOSSafari && (
                                                    <li>현재 브라우저에선 설치가 제한될 수 있어요. <b>Safari</b>로 열어 진행해 주세요.</li>
                                                )}
                                            </ol>
                                            <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
                                                팁: 설치 후에는 홈 화면 아이콘으로 독립 실행(상태바 색상 적용)됩니다.
                                            </div>
                                            <div style={{ marginTop: 16, textAlign: "right" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowIosGuide(false)}
                                                    style={{
                                                        height: 36, padding: "0 16px", borderRadius: 8,
                                                        border: "1px solid #ddd", background: "#f7f7f8", cursor: "pointer"
                                                    }}
                                                >
                                                    닫기
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {/* 1, 2번 항목에 해당되지 않는 경우 */}
                        {
                            !canInstall && !(isIOS && !isStandalone) &&
                            <a href="#none" className="btn_app_download" onClick={move_Android_Market}>
                                <div>
                                    '우끼요' 안드로이드 앱을 설치하려면 여기를 클릭하세요.
                                </div>
                            </a>
                        }

                    </div>
                </nav>
            </Container>
        </div>
    )
}

export default GnbSearch;
